/**
 * certs-shape.js — pure, dependency-free helpers for the env-console
 * Certificates tab (Cert Manager Cycle 3, customer `add-tls-certificate`
 * journey).
 *
 * Everything load-bearing in the Certificates tab lives here so it can be
 * unit-tested without a DOM/runner: it normalizes the two backend cert models
 * into one row shape, classifies status, validates the upload / ACME forms,
 * builds the request payloads, and maps API errors to friendly copy.
 *
 * Backend contracts mirrored (cluster-api routes/certificates.py):
 *   - BYO PEM ("managed"):  GET/POST /api/cluster/certificates/managed,
 *                           GET/DELETE /api/cluster/certificates/managed/<domain>
 *   - ACME (cert-manager):  GET /api/cluster/certificates/acme?cluster=&namespace=
 *
 * The `POST /acme/request` form helpers are gone: every request now goes
 * through `certs-issue-core` (`registrations/ensure` + `/progress`).
 *
 * No imports — keep this module pure so `node --test` can exercise it directly.
 */

// ── Status classification ───────────────────────────────────────────────────
// One normalized status vocabulary across both cert models so the badge/tone
// logic is shared:
//   valid | expiring | expired | pending | failed | unknown
const EXPIRING_SOON_DAYS = 30

const STATUS_BADGES = {
  valid: { label: 'Valid', tone: 'success', icon: 'fa-circle-check' },
  expiring: { label: 'Expiring soon', tone: 'warning', icon: 'fa-clock' },
  expired: { label: 'Expired', tone: 'danger', icon: 'fa-circle-xmark' },
  pending: { label: 'Pending', tone: 'info', icon: 'fa-hourglass-half' },
  failed: { label: 'Failed', tone: 'danger', icon: 'fa-triangle-exclamation' },
  unknown: { label: 'Unknown', tone: 'neutral', icon: 'fa-circle-question' }
}

/** Badge descriptor (label + tone + icon) for a normalized status key. */
export function statusBadge(status) {
  return STATUS_BADGES[status] || STATUS_BADGES.unknown
}

/** Derive a normalized status for a stored BYO PEM cert. */
export function managedStatus(entry) {
  const ci = (entry && entry.certificate_info) || null
  if (!ci) return 'unknown'
  if (ci.is_expired) return 'expired'
  const days = ci.days_until_expiry
  if (typeof days === 'number' && days <= EXPIRING_SOON_DAYS) return 'expiring'
  return 'valid'
}

/** Derive a normalized status for an ACME (cert-manager) Certificate. */
export function acmeStatus(cert) {
  if (!cert) return 'unknown'
  const raw = String(cert.status || '').toLowerCase()
  if (raw === 'failed') return 'failed'
  if (raw === 'expired') return 'expired'
  if (raw === 'expiringsoon') return 'expiring'
  if (raw === 'pending') return 'pending'
  if (raw === 'ready' || cert.ready === true) {
    const days = cert.days_until_expiry
    if (typeof days === 'number' && days <= EXPIRING_SOON_DAYS) return 'expiring'
    return 'valid'
  }
  // Not ready and not an explicit terminal state → still issuing.
  if (cert.ready === false) return 'pending'
  return 'unknown'
}

// ── Normalizers → one row shape ─────────────────────────────────────────────

/** Normalize a `/managed` list entry into a table row. */
export function normalizeManagedCert(entry) {
  const ci = (entry && entry.certificate_info) || {}
  const domain = entry?.domain || ci.common_name || '—'
  return {
    kind: 'managed',
    id: entry?.domain || domain,
    domain,
    dnsNames: ci.san_domains && ci.san_domains.length ? ci.san_domains : [domain],
    issuer: ci.issuer || null,
    notAfter: ci.not_after || null,
    daysUntilExpiry: typeof ci.days_until_expiry === 'number' ? ci.days_until_expiry : null,
    status: managedStatus(entry),
    keyEncrypted: !!(entry && entry.key_encrypted),
    hasCa: !!(entry && entry.has_ca_cert),
    createdAt: entry?.created_at || null,
    raw: entry
  }
}

/** Normalize an ACME `CertificateInfo` into a table row. */
export function normalizeAcmeCert(cert) {
  const dnsNames = cert?.dns_names && cert.dns_names.length ? cert.dns_names : []
  const domain = cert?.common_name || dnsNames[0] || cert?.name || '—'
  const issuer = cert?.issuer_name
    ? `${cert.issuer_name}${cert.issuer_kind ? ` (${cert.issuer_kind})` : ''}`
    : null
  return {
    kind: 'acme',
    id: [cert?.cluster, cert?.namespace, cert?.name].filter(Boolean).join('/'),
    name: cert?.name || null,
    domain,
    dnsNames: dnsNames.length ? dnsNames : [domain],
    issuer,
    notAfter: cert?.not_after || null,
    daysUntilExpiry: typeof cert?.days_until_expiry === 'number' ? cert.days_until_expiry : null,
    status: acmeStatus(cert),
    ready: cert?.ready === true,
    cluster: cert?.cluster || null,
    namespace: cert?.namespace || null,
    secretName: cert?.secret_name || null,
    failureReason: cert?.failure_reason || null,
    createdAt: cert?.created_at || null,
    raw: cert
  }
}

/**
 * Unwrap a `{ certificates: [...] }` (or bare array) data payload into a list
 * of normalized rows. `kind` selects the normalizer.
 */
export function normalizeCertList(data, kind) {
  const list = Array.isArray(data)
    ? data
    : (data && Array.isArray(data.certificates) ? data.certificates : [])
  const fn = kind === 'acme' ? normalizeAcmeCert : normalizeManagedCert
  return list.map(fn)
}

// ── Formatting ──────────────────────────────────────────────────────────────

/** Format an ISO expiry into `{ text, relative }` for the table cell. */
export function formatExpiry(notAfter, daysUntilExpiry) {
  if (!notAfter) return { text: '—', relative: null }
  const d = new Date(notAfter)
  if (isNaN(d.getTime())) return { text: String(notAfter), relative: null }
  const text = d.toISOString().slice(0, 10)
  let relative = null
  if (typeof daysUntilExpiry === 'number') {
    if (daysUntilExpiry < 0) relative = `${Math.abs(daysUntilExpiry)}d ago`
    else if (daysUntilExpiry === 0) relative = 'today'
    else relative = `in ${daysUntilExpiry}d`
  }
  return { text, relative }
}

// ── PEM validation ──────────────────────────────────────────────────────────

const CERT_RE = /-----BEGIN CERTIFICATE-----[\s\S]+-----END CERTIFICATE-----/
const KEY_RE = /-----BEGIN (?:RSA |EC |ENCRYPTED )?PRIVATE KEY-----[\s\S]+-----END (?:RSA |EC |ENCRYPTED )?PRIVATE KEY-----/

/** Cheap client-side guard: does the text look like a PEM cert / key block? */
export function looksLikeCertPem(text) {
  return CERT_RE.test(String(text || ''))
}
export function looksLikeKeyPem(text) {
  return KEY_RE.test(String(text || ''))
}

const DOMAIN_RE = /^(\*\.)?([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/i

/** True for a plausible FQDN (optionally a `*.` wildcard). */
export function isValidDomain(domain) {
  return DOMAIN_RE.test(String(domain || '').trim())
}

// ── Form validation ─────────────────────────────────────────────────────────

/**
 * Validate the BYO PEM upload form. Returns an errors map keyed by field;
 * an empty map means valid.
 */
export function validateUploadForm(form = {}) {
  const errors = {}
  const domain = String(form.domain || '').trim()
  if (!domain) errors.domain = 'Domain is required.'
  else if (!isValidDomain(domain)) errors.domain = 'Enter a valid domain (e.g. app.example.com).'

  if (!String(form.certificate || '').trim()) errors.certificate = 'Certificate (PEM) is required.'
  else if (!looksLikeCertPem(form.certificate)) {
    errors.certificate = 'Paste a PEM block beginning with -----BEGIN CERTIFICATE-----.'
  }

  if (!String(form.privateKey || '').trim()) errors.privateKey = 'Private key (PEM) is required.'
  else if (!looksLikeKeyPem(form.privateKey)) {
    errors.privateKey = 'Paste a PEM private key block.'
  }

  const ca = String(form.caCertificate || '').trim()
  if (ca && !looksLikeCertPem(ca)) {
    errors.caCertificate = 'CA chain must be PEM certificate block(s).'
  }
  return errors
}

// ── Payload builders ────────────────────────────────────────────────────────

/** Build the POST /managed body. Omits an empty CA chain. */
export function buildUploadPayload(form = {}) {
  const payload = {
    domain: String(form.domain || '').trim(),
    certificate: String(form.certificate || '').trim(),
    private_key: String(form.privateKey || '').trim()
  }
  const ca = String(form.caCertificate || '').trim()
  if (ca) payload.ca_certificate = ca
  return payload
}

// ── API error mapping ───────────────────────────────────────────────────────

/**
 * Map an axios/unwrap error to `{ code, message }` the UI can render without
 * re-sniffing status codes. Recognizes the unified-envelope shapes returned by
 * routes/certificates.py:
 *   - 503 secret backend not configured (SOPS key not mounted)
 *   - 503 cert-manager not installed on the target cluster
 *   - 403 forbidden (permission scope)
 *   - 400 validation (errors[] joined)
 */
export function extractApiError(err) {
  const resp = err && err.response
  const status = resp && resp.status
  const body = (resp && resp.data) || {}
  const msg = body.message || (err && err.message) || 'Request failed'
  const errs = Array.isArray(body.errors) ? body.errors.join(' ') : ''
  const blob = `${msg} ${errs}`.toLowerCase()

  if (status === 503) {
    if (blob.includes('secret backend') || blob.includes('sops') || blob.includes('symmetric key')) {
      return {
        code: 'sops_unavailable',
        message: 'Secure key storage is unavailable right now, so the private key can’t be stored encrypted. Please try again shortly or contact support.'
      }
    }
    if (blob.includes('cert-manager') || blob.includes('not_installed') || blob.includes('not installed')) {
      return {
        code: 'cert_manager_unavailable',
        message: 'Automatic (ACME) issuance is not available for this environment yet. You can upload your own certificate instead.'
      }
    }
    return { code: 'service_unavailable', message: msg || 'Service temporarily unavailable.' }
  }
  if (status === 403) {
    return { code: 'forbidden', message: 'You don’t have permission to manage certificates for this environment.' }
  }
  if (status === 401) {
    return { code: 'unauthenticated', message: 'Your session expired. Please sign in again.' }
  }
  if (status === 404) {
    return { code: 'not_found', message: msg || 'Certificate not found.' }
  }
  if (status === 400) {
    return { code: 'bad_request', message: errs || msg || 'Please check the form and try again.' }
  }
  return { code: 'error', message: msg }
}
